/**
 * features/setup/ParentLinkRequests.tsx
 * ------------------------------------------------------------------
 * Office review of SELF-SERVICE parent access requests (parent-initiated
 * via public/parent-link-request.html → submitParentLinkRequest). Runs
 * alongside the key-redemption inbox. For each claimed child the office
 * matches a real pupil (ranked by name + class + DOB, nothing auto-picked,
 * with a DOB agreement indicator), then approves — reusing the exact
 * createParentAccount path so the account/roles/guardian writes are
 * identical to every other onboarding route.
 *
 * WHAT APPROVAL CANNOT DO, surfaced here since Aug 2026.
 *
 * createParentAccount only mints a fresh password when an account has NEVER
 * signed in. For a parent who signed in once and then lost their password,
 * approving them again is a NO-OP: the link is already right, the password is
 * untouched, and they stay locked out. One parent went round that loop three
 * times before anybody could see why, because this screen showed nothing
 * about the state of the account behind the request.
 *
 * So every request now asks checkParentAccountState for the address and says
 * plainly what approving will and will not achieve, and offers the thing that
 * actually helps — sendParentPasswordReset.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CheckCircle2, Loader2, UserCheck, UserX, Inbox, CalendarCheck2, CalendarX2, HelpCircle, KeyRound, AlertTriangle } from 'lucide-react';
import { db, functions, addDoc, updateDoc } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getFormLevel } from '@/lib/grading';
import { Spinner } from '@/components/ui/primitives';
import type { Guardian } from '@/types';
import {
  PARENT_LINK_REQUESTS, rankMatches, dobAgreement, isFullyMatched, relationshipToRole,
  type ParentLinkRequest, type Candidate,
} from '@/lib/parentLinkRequests';

/** What checkParentAccountState returns. `hasEverSignedIn` is the field the
 *  whole fix turns on — see the header note. */
interface AccountState {
  exists: boolean;
  unavailable?: boolean;
  uid?: string;
  disabled?: boolean;
  createdAt?: string | null;
  lastSignInAt?: string | null;
  hasEverSignedIn?: boolean;
  linkedStudentIds?: string[];
}

export default function ParentLinkRequests() {
  const { students, classes } = useData();
  const { userData } = useAuth();

  const [requests, setRequests] = useState<ParentLinkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState<Record<string, Record<number, string>>>({});
  // Per-child fallback level: 0 = strict matches only, 1 = whole class,
  // 2 = whole form (parents often pick the wrong class, rarely the wrong form).
  const [expandLevel, setExpandLevel] = useState<Record<string, number>>({});
  const setLevel = (key: string, lvl: number) => setExpandLevel((p) => ({ ...p, [key]: lvl }));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [result, setResult] = useState<{ parentName: string; email: string; childCount: number; tempPassword?: string; isNewUser?: boolean } | null>(null);
  /* What the account behind each request actually looks like. Keyed by email,
     because two requests from the same parent share one account — which is
     precisely the case that used to be invisible here. */
  const [accountState, setAccountState] = useState<Record<string, AccountState>>({});
  const [resetting, setResetting] = useState<string | null>(null);

  const classNameById = useMemo(() => {
    const m: Record<string, string> = {};
    classes.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [classes]);

  const candidates = useMemo<Candidate[]>(
    () => students
      .filter((s) => s.status !== 'left')
      .map((s) => ({ id: s.id, fullName: s.fullName, classId: s.currentClass, className: classNameById[s.currentClass] || '', dateOfBirth: s.dateOfBirth })),
    [students, classNameById],
  );

  /* Look the addresses up once per batch of pending requests. Firebase Auth
     metadata is not in Firestore, so this cannot come from DataContext. */
  useEffect(() => {
    const emails = [...new Set(requests.map((r) => (r.parentEmail || '').toLowerCase()).filter(Boolean))]
      .filter((e) => !(e in accountState));
    if (!emails.length) return;
    let cancelled = false;
    const check = httpsCallable<{ email: string }, AccountState>(functions, 'checkParentAccountState');
    (async () => {
      for (const email of emails) {
        try {
          const res = await check({ email });
          if (!cancelled) setAccountState((p) => ({ ...p, [email]: res.data }));
        } catch {
          // A lookup failure must not stop the office approving. The card just
          // shows nothing extra, which is where this screen started.
          if (!cancelled) setAccountState((p) => ({ ...p, [email]: { exists: false, unavailable: true } }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [requests]);

  /** Send the parent a link to set a new password. The thing re-approving
   *  cannot do, one click away from where the office already is. */
  async function sendReset(email: string) {
    setResetting(email);
    setMsg(null);
    try {
      const fn = httpsCallable<{ email: string }, { ok: boolean; emailed: boolean }>(functions, 'sendParentPasswordReset');
      const res = await fn({ email });
      setMsg({
        ok: true,
        text: res.data?.emailed
          ? `Password reset emailed to ${email}. The link works once and expires in about an hour.`
          : `Reset generated for ${email}, but the mail server is not configured — nothing was sent.`,
      });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setResetting(null);
    }
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, PARENT_LINK_REQUESTS), where('status', '==', 'pending')),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ParentLinkRequest, 'id'>) }));
        rows.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
        setRequests(rows);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const setMatch = (reqId: string, childIdx: number, studentId: string) =>
    setMatches((prev) => ({ ...prev, [reqId]: { ...(prev[reqId] || {}), [childIdx]: studentId } }));

  async function upsertGuardian(studentId: string, guardianId: string, req: ParentLinkRequest) {
    const stu = students.find((s) => s.id === studentId);
    const guardians: Guardian[] = [...(stu?.admissions?.guardians ?? [])];
    const email = req.parentEmail.toLowerCase();
    const idx = guardians.findIndex((g) => (g.email || '').toLowerCase() === email);
    if (idx >= 0) {
      guardians[idx] = {
        ...guardians[idx],
        fullName: req.parentName || guardians[idx].fullName,
        email: req.parentEmail,
        phone: req.parentPhone || guardians[idx].phone,
        // Never blanks one already on file: an older request carries no
        // occupation at all, and approving it must not erase what the
        // admissions form collected.
        occupation: req.parentOccupation || guardians[idx].occupation,
      };
    } else {
      guardians.push({
        id: guardianId,
        role: relationshipToRole(req.relationship),
        fullName: req.parentName,
        email: req.parentEmail,
        phone: req.parentPhone || undefined,
        occupation: req.parentOccupation || undefined,
        isPrimaryContact: guardians.length === 0,
      });
    }
    /* KEEP parentContact IN STEP. It is the field the fee-reminder sweep
       used to read on its own, and three of the four paths that change a
       guardian's email never wrote it - which is how a parent could give the
       school a working address and still be chased at one the import
       invented from their child's surname. The sender no longer depends on
       it (lib/parentRecipient consults the portal account and the guardians
       first), but older screens still read it, so it must not go stale.

       Only fields the guardian actually has are written. Blanking
       parentContact.email because this guardian gave no address would
       destroy a good one to fix a stale one. */
    const primary = guardians.find((g) => g.isPrimaryContact) ?? guardians[0];
    const contactPatch: Record<string, string> = {};
    if (primary?.fullName) contactPatch['parentContact.guardianName'] = primary.fullName;
    if (primary?.email) contactPatch['parentContact.email'] = primary.email;
    if (primary?.whatsapp) contactPatch['parentContact.whatsapp'] = primary.whatsapp;

    await updateDoc(doc(db, COLLECTIONS.students, studentId), {
      'admissions.guardians': guardians,
      ...contactPatch,
      updatedAt: new Date().toISOString(),
      updatedBy: userData?.uid ?? '',
    });
  }

  async function approve(req: ParentLinkRequest) {
    const m = matches[req.id] || {};
    const studentIds = req.children.map((_, i) => m[i]).filter(Boolean) as string[];
    if (studentIds.length !== req.children.length) return;
    setBusyId(req.id);
    setResult(null);
    try {
      const iso = new Date().toISOString();
      const firstStu = students.find((s) => s.id === studentIds[0]);
      const existingG = (firstStu?.admissions?.guardians ?? []).find(
        (g) => (g.email || '').toLowerCase() === req.parentEmail.toLowerCase(),
      );
      const guardianId = existingG?.id ?? crypto.randomUUID();
      const studentNames = studentIds.map((id) => students.find((s) => s.id === id)?.fullName || '');

      const inviteRef = await addDoc(collection(db, 'reportParentInvitations'), {
        guardianId, guardianName: req.parentName, email: req.parentEmail,
        studentIds, studentNames, status: 'pending',
        invitedBy: userData?.uid ?? '', invitedByName: userData?.displayName ?? '',
        source: 'parentLinkRequest', linkRequestId: req.id, createdAt: iso,
      });

      const createParent = httpsCallable<
        { invitationId: string; email: string; guardianName: string; guardianId: string; studentIds: string[] },
        { ok: boolean; uid: string; isNewUser: boolean; tempPassword?: string }
      >(functions, 'createParentAccount');
      const res = await createParent({ invitationId: inviteRef.id, email: req.parentEmail, guardianName: req.parentName, guardianId, studentIds });

      for (const sid of studentIds) {
        try { await upsertGuardian(sid, guardianId, req); } catch (e) { console.warn('guardian upsert failed', sid, e); }
      }

      await updateDoc(doc(db, PARENT_LINK_REQUESTS, req.id), {
        status: 'approved',
        linkedUid: res.data?.uid ?? null,
        reviewedAt: iso, reviewedBy: userData?.uid ?? '', reviewedByName: userData?.displayName ?? '',
        children: req.children.map((c, i) => ({ ...c, matchedStudentId: m[i] })),
      });

      setResult({ parentName: req.parentName, email: req.parentEmail, childCount: studentIds.length, tempPassword: res.data?.tempPassword, isNewUser: res.data?.isNewUser });
      setMsg({ ok: true, text: `Account linked for ${req.parentName} — ${studentIds.length} child${studentIds.length === 1 ? '' : 'ren'}.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  }

  async function reject(id: string) {
    setBusyId(id);
    try {
      await updateDoc(doc(db, PARENT_LINK_REQUESTS, id), {
        status: 'rejected',
        decisionNote: rejectReason.trim() || null,
        reviewedAt: new Date().toISOString(),
        reviewedBy: userData?.uid ?? '', reviewedByName: userData?.displayName ?? '',
      });
      setRejectingId(null); setRejectReason('');
      setMsg({ ok: true, text: 'Request rejected.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="flex justify-center py-12"><Spinner label="Loading requests…" /></div>;

  return (
    <div className="space-y-4">
      {msg && (
        <div className={`text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300'}`}>{msg.text}</div>
      )}
      {result && (
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-medium"><CheckCircle2 size={16} /> Linked {result.parentName} to {result.childCount} child{result.childCount === 1 ? '' : 'ren'}</div>
          <div className="text-gray-600 dark:text-gray-300 mt-1">
            {result.tempPassword
              ? <>A welcome email with login details was sent to <span className="font-medium">{result.email}</span>. Temporary password: <span className="font-mono">{result.tempPassword}</span></>
              : <>Existing account — the new child was linked and a notice sent to <span className="font-medium">{result.email}</span>.</>}
          </div>
        </div>
      )}

      {requests.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Inbox size={28} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No pending access requests.</p>
          <p className="text-xs mt-1">Parents submit these from the public request page.</p>
        </div>
      ) : requests.map((req) => {
        const m = matches[req.id] || {};
        const ready = isFullyMatched(req.children, m);
        const acct = accountState[(req.parentEmail || '').toLowerCase()];
        /* THE CASE THIS SCREEN USED TO HIDE. An account that has signed in
           before keeps its password through approval, so approving this
           request will not get them in — only a reset will. */
        const lockedOut = !!acct?.exists && !!acct.hasEverSignedIn && !acct.disabled;
        const neverUsed = !!acct?.exists && !acct.hasEverSignedIn && !acct.disabled;
        return (
          <div key={req.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 flex-wrap">
                  {req.parentName}
                  {(req.source === 'parentPortalAddChild' || req.linkedUid) && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      Existing parent · adding a child
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {req.parentEmail} · {req.parentPhone} · {req.relationship || 'guardian'}
                  {req.parentOccupation ? ` · ${req.parentOccupation}` : ''}
                  {' · '}{new Date(req.createdAt).toLocaleDateString()}
                </div>
                {req.note && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">“{req.note}”</div>}
              </div>
            </div>

            {lockedOut && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-200">
                  <AlertTriangle size={15} />
                  This parent already has a working account — approving again will not let them in
                </div>
                <div className="text-amber-900/80 dark:text-amber-200/80 text-xs mt-1 leading-relaxed">
                  They last signed in on{' '}
                  <span className="font-medium">
                    {acct?.lastSignInAt ? new Date(acct.lastSignInAt).toLocaleDateString() : 'a previous date'}
                  </span>
                  , and are linked to {acct?.linkedStudentIds?.length ?? 0} child
                  {(acct?.linkedStudentIds?.length ?? 0) === 1 ? '' : 'ren'}. An account that has been
                  used keeps its password through approval, so this is almost always a forgotten
                  password. Send a reset — then approve only if this request adds a NEW child.
                </div>
                <button
                  onClick={() => sendReset((req.parentEmail || '').toLowerCase())}
                  disabled={resetting === (req.parentEmail || '').toLowerCase()}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
                >
                  {resetting === (req.parentEmail || '').toLowerCase()
                    ? <Loader2 size={13} className="animate-spin" />
                    : <KeyRound size={13} />}
                  Send password reset
                </button>
              </div>
            )}
            {neverUsed && (
              <div className="rounded-lg border border-sky-200 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-900/20 p-3 text-xs text-sky-900 dark:text-sky-200">
                An account exists for this address but has <span className="font-medium">never been signed in to</span>.
                Approving will issue a fresh password and email it, which is the right thing to do here.
              </div>
            )}

            <div className="space-y-2">
              {req.children.map((child, i) => {
                const ranked = rankMatches(child, candidates);
                const key = `${req.id}:${i}`;
                const level = expandLevel[key] ?? 0;
                // Fallback ladder: pupils in the parent's chosen CLASS, then —
                // if the parent got the class wrong — the whole FORM (they're
                // far likelier to know the form than the exact class).
                const clsNorm = (child.className || '').toLowerCase().replace(/\s+/g, ' ').trim();
                const classCandidates = candidates
                  .filter((c) => (child.classId && c.classId === child.classId)
                    || (!!clsNorm && (c.className || '').toLowerCase().replace(/\s+/g, ' ').trim() === clsNorm))
                  .sort((a, b) => a.fullName.localeCompare(b.fullName));
                const childForm = getFormLevel(child.className);
                const classIds = new Set(classCandidates.map((c) => c.id));
                const formCandidates = childForm
                  ? candidates
                      .filter((c) => getFormLevel(c.className) === childForm && !classIds.has(c.id))
                      .sort((a, b) => a.fullName.localeCompare(b.fullName))
                  : [];
                const chosen = m[i];
                const chosenCand = candidates.find((c) => c.id === chosen);
                const dob = chosenCand ? dobAgreement(child.dateOfBirth, chosenCand.dateOfBirth) : 'unknown';
                // System format is surname-first — display the parent's claim
                // the same way so it lines up visually with the pupil list.
                const claimName = `${child.surname} ${child.firstName}`.trim();
                return (
                  <div key={i} className="bg-gray-50 dark:bg-gray-800/40 rounded-lg p-2.5">
                    <div className="text-sm text-gray-700 dark:text-gray-200">
                      <span className="font-medium">{claimName}</span>
                      <span className="text-gray-400"> · {child.className || '—'} · DOB {child.dateOfBirth}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <select
                        value={chosen || ''}
                        onChange={(e) => setMatch(req.id, i, e.target.value)}
                        className="flex-1 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5"
                      >
                        <option value="">Match to a pupil…</option>
                        {ranked.length > 0 && (
                          <optgroup label="Matches (2+ of name / class / DOB)">
                            {ranked.map((c) => {
                              const d = dobAgreement(child.dateOfBirth, c.dateOfBirth);
                              const tag = d === 'match' ? '✓ DOB' : d === 'mismatch' ? '✗ DOB differs' : '– no DOB';
                              return <option key={c.id} value={c.id}>{c.fullName} · {c.className || '—'} · {tag}</option>;
                            })}
                          </optgroup>
                        )}
                        {level >= 1 && (
                          <optgroup label={`All pupils in ${child.className || 'this class'}`}>
                            {classCandidates.map((c) => (
                              <option key={c.id} value={c.id}>{c.fullName}</option>
                            ))}
                          </optgroup>
                        )}
                        {level >= 2 && (
                          <optgroup label={`All pupils in Form ${childForm || '?'}`}>
                            {formCandidates.map((c) => (
                              <option key={c.id} value={c.id}>{c.fullName} · {c.className || '—'}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      {chosen && (
                        <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
                          dob === 'match' ? 'text-emerald-600 dark:text-emerald-400' : dob === 'mismatch' ? 'text-rose-600 dark:text-rose-400' : 'text-gray-400'
                        }`}>
                          {dob === 'match' ? <CalendarCheck2 size={13} /> : dob === 'mismatch' ? <CalendarX2 size={13} /> : <HelpCircle size={13} />}
                          {dob === 'match' ? 'DOB matches' : dob === 'mismatch' ? 'DOB differs' : 'no DOB on file'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-xs text-gray-400">
                        {ranked.length === 0
                          ? 'No strict match — the parent may have the class wrong. Expand to the class, then the whole form.'
                          : `${ranked.length} matching pupil${ranked.length === 1 ? '' : 's'} (2+ of name / class / DOB).`}
                      </span>
                      <div className="flex items-center gap-3 shrink-0">
                        {level === 0 && (
                          <button type="button" onClick={() => setLevel(key, 1)} className="text-xs text-primary hover:underline">
                            Show all in {child.className || 'class'}
                          </button>
                        )}
                        {level === 1 && (
                          <button type="button" onClick={() => setLevel(key, 2)} className="text-xs text-primary hover:underline">
                            Expand to whole Form {childForm || ''}
                          </button>
                        )}
                        {level >= 1 && (
                          <button type="button" onClick={() => setLevel(key, 0)} className="text-xs text-gray-400 hover:underline">Hide</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              {rejectingId === req.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejection"
                    className="flex-1 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5"
                  />
                  <button onClick={() => reject(req.id)} disabled={busyId === req.id} className="text-sm px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">Confirm reject</button>
                  <button onClick={() => { setRejectingId(null); setRejectReason(''); }} className="text-sm px-3 py-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</button>
                </div>
              ) : (
                <>
                  <button onClick={() => setRejectingId(req.id)} disabled={busyId === req.id} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"><UserX size={15} /> Reject</button>
                  <button onClick={() => approve(req)} disabled={!ready || busyId === req.id} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primaryDark disabled:opacity-50" title={ready ? '' : 'Match every child to a pupil first'}>
                    {busyId === req.id ? <Loader2 size={15} className="animate-spin" /> : <UserCheck size={15} />} Approve &amp; link
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
